import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './EditCourse.css';

const EditCourse = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, { state: { activeTab: location.state.tab || 'courses' } });
    } else {
      navigate(-1);
    }
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);

  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    category_id: '',
    thumbnail_url: '',
    is_published: false,
  });

  const [videos, setVideos] = useState([]);
  const [deletedVideoIds, setDeletedVideoIds] = useState([]);

  // Course materials state
  const [materials, setMaterials] = useState([]);
  const [materialTitle, setMaterialTitle] = useState('');
  const [materialFile, setMaterialFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [viewingMaterial, setViewingMaterial] = useState(null); // { title, blobUrl }
  const fileInputRef = useRef(null);

  // Check permissions
  const isAdmin = user?.role === 'admin' || user?.role === 'sub-admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const courseRes = await api.get(`/courses/${id}`);
        const course = courseRes.data;

        if (!isAdmin && course.instructor_id !== user?.id) {
          navigate('/');
          return;
        }

        setCourseData({
          title: course.title || '',
          description: course.description || '',
          category_id: course.category_id || '',
          thumbnail_url: course.thumbnail_url || '',
          is_published: course.is_published || false,
        });

        setVideos(
          (course.videos || []).map((v) => ({
            id: v.id,
            title: v.title || '',
            description: v.description || '',
            youtube_url: v.youtube_url || '',
            duration: v.duration || '',
            order_index: v.order_index,
            isNew: false,
          }))
        );

        const catRes = await api.get('/categories');
        setCategories(catRes.data);
      } catch (err) {
        console.error('Error fetching course:', err);
        setError('Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user?.id, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch materials separately after load
  useEffect(() => {
    if (!id) return;
    api.get(`/courses/${id}/materials`)
      .then((res) => setMaterials(res.data || []))
      .catch(() => setMaterials([]));
  }, [id]);

  const handleCourseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourseData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...videos];
    newVideos[index][field] = value;
    setVideos(newVideos);
  };

  const addVideo = () => {
    setVideos([
      ...videos,
      {
        id: null,
        title: '',
        description: '',
        youtube_url: '',
        duration: '',
        order_index: videos.length,
        isNew: true,
      },
    ]);
  };

  const removeVideo = (index) => {
    const video = videos[index];
    if (video.id && !video.isNew) {
      setDeletedVideoIds([...deletedVideoIds, video.id]);
    }
    setVideos(videos.filter((_, i) => i !== index));
  };

  const moveVideo = (index, direction) => {
    const newVideos = [...videos];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newVideos.length) return;

    [newVideos[index], newVideos[newIndex]] = [newVideos[newIndex], newVideos[index]];
    newVideos.forEach((v, i) => { v.order_index = i; });
    setVideos(newVideos);
  };

  const extractYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await api.put(`/courses/${id}`, {
        title: courseData.title,
        description: courseData.description,
        category_id: courseData.category_id,
        thumbnail_url: courseData.thumbnail_url,
        ...(isAdmin && { is_published: courseData.is_published }),
      });

      for (const videoId of deletedVideoIds) {
        try {
          await api.delete(`/videos/${videoId}`);
        } catch (err) {
          console.error('Error deleting video:', err);
        }
      }

      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        const youtubeId = extractYoutubeId(video.youtube_url);

        const videoData = {
          course_id: id,
          title: video.title,
          description: video.description,
          youtube_url: video.youtube_url,
          youtube_id: youtubeId,
          duration: video.duration ? parseInt(video.duration, 10) : null,
          order_index: i,
        };

        if (video.id && !video.isNew) {
          await api.put(`/videos/${video.id}`, videoData);
        } else if (video.title && video.youtube_url) {
          await api.post('/videos', videoData);
        }
      }

      setSuccess('Course updated successfully!');
      setDeletedVideoIds([]);

      const refreshed = await api.get(`/courses/${id}`);
      setVideos(
        (refreshed.data.videos || []).map((v) => ({
          id: v.id,
          title: v.title || '',
          description: v.description || '',
          youtube_url: v.youtube_url || '',
          duration: v.duration || '',
          order_index: v.order_index,
          isNew: false,
        }))
      );
    } catch (err) {
      console.error('Error saving course:', err);
      setError(err.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  // ---- Course Materials ----

  const handleUploadMaterial = async (e) => {
    e.preventDefault();
    if (!materialFile) {
      setUploadError('Please select a PDF file.');
      return;
    }
    if (!materialTitle.trim()) {
      setUploadError('Please enter a title for this material.');
      return;
    }

    setUploading(true);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', materialFile);
    formData.append('title', materialTitle.trim());

    try {
      const res = await api.post(`/courses/${id}/materials`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMaterials((prev) => [...prev, res.data]);
      setMaterialTitle('');
      setMaterialFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Delete this material? This cannot be undone.')) return;
    try {
      await api.delete(`/courses/${id}/materials/${materialId}`);
      setMaterials((prev) => prev.filter((m) => m.id !== materialId));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete material');
    }
  };

  const handleViewMaterial = async (material) => {
    try {
      const res = await api.get(`/courses/${id}/materials/${material.id}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setViewingMaterial({ title: material.title, blobUrl });
    } catch (err) {
      alert('Failed to load PDF');
    }
  };

  const closeViewer = () => {
    if (viewingMaterial?.blobUrl) {
      URL.revokeObjectURL(viewingMaterial.blobUrl);
    }
    setViewingMaterial(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page edit-course-page">
      <div className="edit-course-container">
        <div className="edit-header">
          <button onClick={handleBack} className="back-btn">
            &larr; Back
          </button>
          <h1>Edit Course</h1>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Course Details Section */}
          <div className="form-section">
            <h2>Course Details</h2>

            <div className="form-group">
              <label>Course Title *</label>
              <input
                type="text"
                name="title"
                value={courseData.title}
                onChange={handleCourseChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={courseData.description}
                onChange={handleCourseChange}
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category_id"
                  value={courseData.category_id}
                  onChange={handleCourseChange}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Thumbnail URL</label>
                <input
                  type="url"
                  name="thumbnail_url"
                  value={courseData.thumbnail_url}
                  onChange={handleCourseChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            {isAdmin && (
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={courseData.is_published}
                    onChange={handleCourseChange}
                  />
                  Published (visible to students)
                </label>
              </div>
            )}
          </div>

          {/* Videos Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Course Videos ({videos.length})</h2>
              <button type="button" className="btn-secondary" onClick={addVideo}>
                + Add Video
              </button>
            </div>

            {videos.length === 0 ? (
              <p className="no-videos">No videos yet. Click "Add Video" to add lessons.</p>
            ) : (
              <div className="videos-list">
                {videos.map((video, index) => (
                  <div key={video.id || `new-${index}`} className="video-edit-card">
                    <div className="video-edit-header">
                      <span className="video-number">Lesson {index + 1}</span>
                      <div className="video-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => moveVideo(index, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => moveVideo(index, 1)}
                          disabled={index === videos.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-delete"
                          onClick={() => removeVideo(index)}
                          title="Delete video"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    <div className="video-edit-body">
                      <div className="form-group">
                        <label>Video Title</label>
                        <input
                          type="text"
                          value={video.title}
                          onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                          placeholder="Enter video title"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={video.description}
                          onChange={(e) => handleVideoChange(index, 'description', e.target.value)}
                          rows="2"
                          placeholder="Brief description of this lesson"
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>YouTube URL</label>
                          <input
                            type="url"
                            value={video.youtube_url}
                            onChange={(e) => handleVideoChange(index, 'youtube_url', e.target.value)}
                            placeholder="https://youtube.com/watch?v=..."
                          />
                        </div>
                        <div className="form-group">
                          <label>Duration (seconds)</label>
                          <input
                            type="number"
                            value={video.duration}
                            onChange={(e) => handleVideoChange(index, 'duration', e.target.value)}
                            placeholder="600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleBack}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Course Materials Section (outside form — separate upload action) */}
        <div className="form-section materials-section">
          <h2>Course Materials (PDF)</h2>
          <p className="materials-note">
            Upload PDF documents as course materials. Students can view them online but cannot download them.
          </p>

          {/* Upload Form */}
          <form onSubmit={handleUploadMaterial} className="material-upload-form">
            <div className="form-row">
              <div className="form-group">
                <label>Material Title *</label>
                <input
                  type="text"
                  value={materialTitle}
                  onChange={(e) => setMaterialTitle(e.target.value)}
                  placeholder="e.g. Week 1 Reading"
                />
              </div>
              <div className="form-group">
                <label>PDF File *</label>
                <input
                  type="file"
                  accept=".pdf"
                  ref={fileInputRef}
                  onChange={(e) => setMaterialFile(e.target.files[0] || null)}
                />
              </div>
            </div>
            {uploadError && <p className="error-message">{uploadError}</p>}
            <button type="submit" className="btn-primary" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload PDF'}
            </button>
          </form>

          {/* Materials List */}
          {materials.length === 0 ? (
            <p className="no-videos" style={{ marginTop: '16px' }}>No materials uploaded yet.</p>
          ) : (
            <div className="materials-list">
              {materials.map((mat) => (
                <div key={mat.id} className="material-item">
                  <div className="material-info">
                    <span className="material-icon">PDF</span>
                    <div>
                      <p className="material-title">{mat.title}</p>
                      <p className="material-meta">
                        {mat.file_name}
                        {mat.file_size ? ` · ${formatFileSize(mat.file_size)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="material-actions">
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={() => handleViewMaterial(mat)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="btn-danger btn-small"
                      onClick={() => handleDeleteMaterial(mat.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {viewingMaterial && (
        <div className="pdf-viewer-overlay" onClick={closeViewer}>
          <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <h3>{viewingMaterial.title}</h3>
              <button type="button" className="modal-close" onClick={closeViewer}>
                &times;
              </button>
            </div>
            <div className="pdf-viewer-body">
              <iframe
                src={viewingMaterial.blobUrl}
                title={viewingMaterial.title}
                className="pdf-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCourse;
